import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ExternalLink, MapPin, Trash2, RefreshCw, Plus, Loader2, MailCheck, AlertTriangle } from "lucide-react";
import { api, assetUrl, errorText } from "../lib/api";
import { MemorialFields, validateFields } from "../components/MemorialFields";
import { ModelPicker } from "../components/ModelPicker";
import { checkFile } from "../components/PhotoPicker";
import { StonePreview } from "../scene/StonePreview";
import { LinksBlock } from "../scene/ReadyPanel";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { NotAvailable } from "./MemorialPage";

const Card = ({ title, children, testId, lead }) => (
  <section className="rounded-3xl border bg-[hsl(var(--card))] p-6 sm:p-8" data-testid={testId}>
    <h2 className="font-heading text-2xl">{title}</h2>
    {lead && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{lead}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

const FileButton = ({ onFile, children, className, testId, disabled }) => {
  const { t } = useTranslation();
  const ref = useRef();
  return (
    <>
      <button type="button" className={className} onClick={() => ref.current?.click()} data-testid={testId} disabled={disabled}>{children}</button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`${testId}-input`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (checkFile(t, f)) onFile(f); }} />
    </>
  );
};

const CaptionField = ({ token, g, i, onSaved }) => {
  const { t } = useTranslation();
  const [v, setV] = useState(g.caption || "");
  const save = async () => {
    if (v === (g.caption || "")) return;
    try {
      const { data } = await api.patch(`/manage/${token}/gallery/${g.id}`, { caption: v });
      onSaved(data);
      toast.success(t("manage.caption_saved"));
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };
  return <input type="text" maxLength={80} dir="auto" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} placeholder={t("wizard.caption_placeholder")} aria-label={t("wizard.caption")} className="field-input !py-1.5 !px-2.5 !text-xs mt-2" data-testid={`manage-caption-${i}`} />;
};

export default function ManagePage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [form, setForm] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [email, setEmail] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.append(meta, robots);
    return () => { meta.remove(); robots.remove(); };
  }, []);

  const apply = (data) => {
    setM(data);
    setForm({ name: data.name, surname: data.surname, species: data.species, breed: data.breed, birth_date: data.birth_date || "", death_date: data.death_date || "", epitaph: data.epitaph, epitaph_font: data.epitaph_font || "classic", model: data.model, visibility: data.visibility, consent_public: false });
  };
  useEffect(() => {
    api.get(`/manage/${token}`).then(({ data }) => apply(data)).catch(() => setInvalid(true));
  }, [token]);

  if (invalid) return <NotAvailable title={t("manage.invalid_title")} text={t("manage.invalid_text")} testId="manage-invalid" />;
  if (!m || !form) return <div className="min-h-screen grid place-items-center"><div className="boot-glow" /></div>;

  const save = async () => {
    const err = validateFields(form);
    if (err) return toast.error(t(`errors.${err}`));
    setBusy(true);
    try {
      const { data } = await api.patch(`/manage/${token}`, form);
      apply(data);
      toast.success(t("manage.saved"));
    } catch (e) {
      toast.error(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const upload = async (path, file, method = "post") => {
    setUploading(true);
    const fd = new FormData();
    fd.append("photo", file);
    try {
      const { data } = await api[method](`/manage/${token}${path}`, fd);
      apply(data);
    } catch (e) {
      toast.error(errorText(t, e));
    } finally {
      setUploading(false);
    }
  };
  const removeImage = async (id) => {
    try {
      const { data } = await api.delete(`/manage/${token}/gallery/${id}`);
      apply(data);
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };
  const toggleMain = async (v) => {
    const { data } = await api.patch(`/manage/${token}`, { include_main_in_gallery: v });
    apply(data);
  };
  const sendEmail = async () => {
    try {
      const { data } = await api.post(`/manage/${token}/email`, { email });
      if (data.verification_email_sent) toast.success(t("manage.recovery_sent"));
      else toast.error(t("errors.email_unavailable"));
      const r = await api.get(`/manage/${token}`);
      apply(r.data);
      setEmail("");
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };
  const remove = async () => {
    await api.delete(`/manage/${token}`);
    toast.success(t("manage.deleted"));
    navigate("/");
  };

  const gallery = m.gallery.filter((g) => g.kind === "gallery");
  const used = gallery.length;
  const stone = { ...form, slug: m.slug, photoUrl: m.photo ? assetUrl(m.photo.thumb) : null, flowers_count: 0 };

  return (
    <main id="main" className="mx-auto max-w-6xl px-5 sm:px-8 pt-32 pb-24" data-testid="manage-page">
      <p className="eyebrow">{t("nav.memorials")}</p>
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-3" dir="auto" data-testid="manage-title">{t("manage.title", { name: m.name })}</h1>
      <p className="text-[hsl(var(--muted-foreground))] mt-3 max-w-2xl">{t("manage.lead")}</p>
      {m.status !== "active" && (
        <p className="mt-6 flex gap-3 items-start rounded-2xl bg-[#f4e6cc] text-[#5b4626] p-4 text-sm" data-testid="manage-status-banner">
          <AlertTriangle size={18} className="shrink-0" /> {t(m.status === "hidden" ? "manage.status_hidden" : "manage.status_under_review")}
        </p>
      )}
      <div className="flex flex-wrap gap-3 mt-8">
        <Link to={`/m/${m.slug}`} className="btn-primary" data-testid="manage-view-page-btn"><ExternalLink size={16} /> {t("manage.view_page")}</Link>
        <Link to={`/explore/${m.environment}?m=${m.slug}`} className="btn-ghost" data-testid="manage-view-stone-btn"><MapPin size={16} /> {t("manage.view_stone")}</Link>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 mt-12">
        <div className="lg:col-span-7 space-y-8">
          <Card title={t("manage.edit_title")} testId="manage-details-card">
            <MemorialFields value={form} onChange={setForm} prefix="manage" />
            <div className="mt-8">
              <p className="field-label">{t("wizard.steps.stone")}</p>
              <ModelPicker value={form.model} onChange={(v) => setForm({ ...form, model: v })} prefix="manage-model" />
            </div>
            <fieldset className="mt-8 space-y-3">
              <legend className="field-label">{t("manage.visibility")}</legend>
              {["unlisted", "public"].map((v) => (
                <label key={v} className={`flex gap-3 rounded-2xl border p-4 cursor-pointer ${form.visibility === v ? "border-[var(--gold)]" : ""}`} data-testid={`manage-visibility-${v}`}>
                  <input type="radio" checked={form.visibility === v} onChange={() => setForm({ ...form, visibility: v })} className="mt-1 accent-[#c9a46a]" />
                  <span><span className="block font-semibold">{t(`wizard.visibility_${v}`)}</span><span className="block text-sm text-[hsl(var(--muted-foreground))]">{t(`wizard.visibility_${v}_desc`)}</span></span>
                </label>
              ))}
              {form.visibility === "public" && m.visibility !== "public" && (
                <label className="flex gap-3 items-start text-sm">
                  <Checkbox checked={form.consent_public} onCheckedChange={(v) => setForm({ ...form, consent_public: !!v })} data-testid="manage-consent-checkbox" className="mt-0.5" />
                  {t("wizard.consent_public")}
                </label>
              )}
            </fieldset>
            <button className="btn-gold mt-8" onClick={save} disabled={busy} data-testid="manage-save-btn">{busy && <Loader2 size={16} className="animate-spin" />} {t("manage.save")}</button>
          </Card>

          <Card title={t("manage.gallery_title")} lead={t("manage.gallery_lead", { used, max: m.gallery_slots })} testId="manage-gallery-card">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {m.photo && (
                <figure className="rounded-2xl border p-2" data-testid="manage-main-photo">
                  <img src={assetUrl(m.photo.thumb)} alt="" className="aspect-square w-full object-cover rounded-xl" />
                  <figcaption className="text-xs font-semibold mt-2 px-1">{t("manage.main_photo")}</figcaption>
                </figure>
              )}
              {gallery.map((g, i) => (
                <figure key={g.id} className="rounded-2xl border p-2" data-testid={`manage-gallery-item-${i}`}>
                  <img src={assetUrl(g.thumb)} alt="" className="aspect-square w-full object-cover rounded-xl" />
                  {g.moderation_status !== "approved" && <p className="text-[11px] mt-1 px-1 text-[var(--clay)]">{t(g.moderation_status === "flagged" ? "manage.moderation_flagged" : "manage.moderation_pending")}</p>}
                  <CaptionField token={token} g={g} i={i} onSaved={(d) => setM(d)} />
                  <div className="flex gap-1 mt-2">
                    <FileButton className="btn-ghost !px-2 !py-1.5 text-xs flex-1" onFile={(f) => upload(`/gallery/${g.id}`, f, "put")} testId={`manage-replace-${i}`}><RefreshCw size={12} /> {t("manage.replace")}</FileButton>
                    <button className="btn-ghost !px-2 !py-1.5 text-xs" onClick={() => removeImage(g.id)} aria-label={t("manage.remove")} data-testid={`manage-remove-${i}`}><Trash2 size={12} /></button>
                  </div>
                </figure>
              ))}
              {used < m.gallery_slots && (
                <FileButton className="rounded-2xl border-2 border-dashed aspect-square grid place-items-center text-sm text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--accent))]" onFile={(f) => upload("/gallery", f)} testId="manage-add-photo" disabled={uploading}>
                  {uploading ? <Loader2 className="animate-spin" /> : <span className="flex flex-col items-center gap-2"><Plus /> {t("manage.add_photo")}</span>}
                </FileButton>
              )}
            </div>
            {used >= m.gallery_slots && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-4">{t("manage.gallery_full")}</p>}
            <div className="flex flex-wrap items-center gap-6 mt-6">
              <FileButton className="btn-ghost !py-2.5" onFile={(f) => upload("/photo", f)} testId="manage-replace-main"><RefreshCw size={14} /> {t("manage.replace_photo")}</FileButton>
              {m.photo && (
                <label className="flex items-center gap-3 text-sm">
                  <Switch checked={m.include_main_in_gallery} onCheckedChange={toggleMain} data-testid="manage-include-main-switch" /> {t("manage.include_main")}
                </label>
              )}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-5 space-y-8">
          <StonePreview memorial={stone} env={m.environment} testId="manage-stone-preview" height="h-[380px]" />
          <Card title={t("manage.warning_title")} testId="manage-links-card">
            <LinksBlock name={m.name} slug={m.slug} token={token} />
          </Card>
          <Card title={t("manage.recovery_title")} lead={t("manage.recovery_lead")} testId="manage-recovery-card">
            {m.recovery_email && (
              <p className="text-sm mb-4 flex items-center gap-2" data-testid="manage-recovery-status">
                <MailCheck size={16} /> {t(m.recovery_email_verified ? "manage.recovery_verified" : "manage.recovery_pending", { email: m.recovery_email })}
              </p>
            )}
            <div className="flex gap-2">
              <input type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("wizard.email_label")} data-testid="manage-email-input" />
              <button className="btn-primary !px-4" onClick={sendEmail} disabled={!email} data-testid="manage-email-btn">{t("manage.recovery_send")}</button>
            </div>
          </Card>
          <Card title={t("manage.danger_title")} lead={t("manage.danger_text")} testId="manage-danger-card">
            <button className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold bg-[hsl(var(--destructive))] text-white hover:opacity-90" onClick={() => setConfirmDelete(true)} data-testid="manage-delete-btn">
              <Trash2 size={16} /> {t("common.delete")}
            </button>
          </Card>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-3xl" data-testid="manage-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl font-normal">{t("manage.delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("manage.delete_confirm_text")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manage-delete-cancel-btn">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[hsl(var(--destructive))]" data-testid="manage-delete-confirm-btn">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
